/**
 * Cube-coordinate hex algebra — pure, render-free, rules-free.
 *
 * We store hexes in **cube coordinates** `{ q, r, s }` with the invariant
 * `q + r + s === 0`. Cube coords make distance, neighbors, rotation, and line
 * drawing fall out as plain vector math; axial `(q, r)` is just cube with `s`
 * left implicit (`s = -q - r`). This is the canonical Red Blob Games model
 * (https://www.redblobgames.com/grids/hexagons/) and the shared vocabulary the
 * range, pathfinding, and (later) pixel-layout layers all build on.
 *
 * Hexes are treated as **immutable value objects**: every operation returns a
 * fresh `Hex`, none mutate. Use {@link hexKey} when you need a `Map`/`Set` key.
 */

/** A hex in cube coordinates. Invariant: `q + r + s === 0`. */
export type Hex = {
  readonly q: number;
  readonly r: number;
  readonly s: number;
}

/**
 * Collapse `-0` to `0`. Negation/subtraction readily produce `-0`, which
 * `Object.is`-based equality (`toBe`/`toEqual`, `Map` keys via `===` are fine,
 * but structural compares are not) treats as distinct from `0`. Normalising at
 * construction keeps `Hex` values canonical so equality is never surprising.
 */
function z(n: number): number {
  return n === 0 ? 0 : n;
}

/**
 * Construct a hex from cube coords. `s` defaults to `-q - r` (the axial case).
 * Throws if an explicit `s` violates `q + r + s === 0` — a wrong `s` is a bug,
 * not a coordinate we want to silently round away.
 */
export function hex(q: number, r: number, s: number = -q - r): Hex {
  if (Math.round(q + r + s) !== 0) {
    throw new Error(`invalid hex: q + r + s must be 0 (got ${q} + ${r} + ${s} = ${q + r + s})`);
  }
  return { q: z(q), r: z(r), s: z(s) };
}

/** Construct a hex from axial coords `(q, r)`; `s` is derived. */
export function axial(q: number, r: number): Hex {
  return { q: z(q), r: z(r), s: z(-q - r) };
}

/** Stable string key (`"q,r"`) for use in `Map`/`Set`. `s` is redundant. */
export function hexKey(h: Hex): string {
  return `${h.q},${h.r}`;
}

/** Inverse of {@link hexKey}: parse `"q,r"` back into a `Hex` (`s` derived). */
export function parseHexKey(k: string): Hex {
  const [q, r] = k.split(",").map(Number);
  if (q === undefined || r === undefined || Number.isNaN(q) || Number.isNaN(r)) {
    throw new Error(`invalid hex key: ${JSON.stringify(k)}`);
  }
  return axial(q, r);
}

export function equals(a: Hex, b: Hex): boolean {
  return a.q === b.q && a.r === b.r && a.s === b.s;
}

export function add(a: Hex, b: Hex): Hex {
  return { q: z(a.q + b.q), r: z(a.r + b.r), s: z(a.s + b.s) };
}

export function subtract(a: Hex, b: Hex): Hex {
  return { q: z(a.q - b.q), r: z(a.r - b.r), s: z(a.s - b.s) };
}

/** Scale a hex vector by an integer factor. */
export function scale(h: Hex, k: number): Hex {
  return { q: z(h.q * k), r: z(h.r * k), s: z(h.s * k) };
}

/**
 * The six unit direction vectors, in counter-clockwise order starting from the
 * "east" neighbour. Index with {@link direction}; step with {@link neighbor}.
 */
export const DIRECTIONS: readonly Hex[] = [
  { q: 1, r: 0, s: -1 },
  { q: 1, r: -1, s: 0 },
  { q: 0, r: -1, s: 1 },
  { q: -1, r: 0, s: 1 },
  { q: -1, r: 1, s: 0 },
  { q: 0, r: 1, s: -1 },
];

/** Direction vector for index `i`, wrapped into `[0, 6)` (negatives welcome). */
export function direction(i: number): Hex {
  const d = DIRECTIONS[((i % 6) + 6) % 6];
  // Safe: index is forced into [0, 6) and DIRECTIONS has length 6.
  return d as Hex;
}

/** The neighbour of `h` one step in direction `i`. */
export function neighbor(h: Hex, i: number): Hex {
  return add(h, direction(i));
}

/** All six neighbours of `h`, in {@link DIRECTIONS} order. */
export function neighbors(h: Hex): Hex[] {
  return DIRECTIONS.map((d) => add(h, d));
}

/** Cube length of a hex vector = grid distance from the origin. */
export function length(h: Hex): number {
  return (Math.abs(h.q) + Math.abs(h.r) + Math.abs(h.s)) / 2;
}

/** Grid distance (number of steps) between two hexes. */
export function distance(a: Hex, b: Hex): number {
  return length(subtract(a, b));
}

/**
 * Round fractional cube coords to the nearest valid hex, repairing the
 * `q + r + s === 0` invariant by resetting whichever component drifted most.
 * The bridge from continuous space (pixel→hex, interpolation) back to the grid.
 */
export function round(q: number, r: number, s: number): Hex {
  let rq = Math.round(q);
  let rr = Math.round(r);
  let rs = Math.round(s);
  const dq = Math.abs(rq - q);
  const dr = Math.abs(rr - r);
  const ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) {
    rq = -rr - rs;
  } else if (dr > ds) {
    rr = -rq - rs;
  } else {
    rs = -rq - rr;
  }
  return { q: z(rq), r: z(rr), s: z(rs) };
}

/** Linear interpolation between two hexes (fractional result, pre-{@link round}). */
function lerp(a: Hex, b: Hex, t: number): { q: number; r: number; s: number } {
  return {
    q: a.q + (b.q - a.q) * t,
    r: a.r + (b.r - a.r) * t,
    s: a.s + (b.s - a.s) * t,
  };
}

/**
 * The straight line of hexes from `a` to `b` inclusive (length
 * `distance(a, b) + 1`). Samples the segment at each step and rounds; a tiny
 * nudge keeps samples off exact edges so ties resolve consistently.
 */
export function line(a: Hex, b: Hex): Hex[] {
  const n = distance(a, b);
  if (n === 0) return [a];
  // Nudge endpoints into the same hex but off shared edges (Red Blob's epsilon).
  const aNudge: Hex = { q: a.q + 1e-6, r: a.r + 1e-6, s: a.s - 2e-6 };
  const bNudge: Hex = { q: b.q + 1e-6, r: b.r + 1e-6, s: b.s - 2e-6 };
  const out: Hex[] = [];
  for (let i = 0; i <= n; i++) {
    const p = lerp(aNudge, bNudge, i / n);
    out.push(round(p.q, p.r, p.s));
  }
  return out;
}
